/**
 * ✅ RESTAURANT MUTFAK VE YAZDIR İŞLEMLERİ HOOK'U
 * 
 * Bu hook, restoran modunda mutfak ve yazdırma işlemlerini yönetir:
 * - handlePrintToKitchen: Mutfağa sipariş yazdır
 * - handlePrintCheck: Adisyon fişi yazdır
 * - handleBackButtonClick: Geri butonu kontrolü (yazdırılmamış ürün kontrolü)
 * - handleContinueWithoutPrinting: Yazdırmadan devam et
 * - handlePrintToKitchenAndGoBack: İlave ürünleri yazdır ve geri dön
 * - handlePrintCancellationAndGoBack: İptal fişi yazdır ve geri dön
 * - handlePrintBothAndGoBack: Hem ilave hem iptal yazdır
 * - handleCloseTable: Adisyon iptal (masayı kapat)
 * 
 * QuickSaleScreen.tsx'den ayrıştırıldı - Ocak 2026
 */

import { useCallback, useRef } from 'react';
import type { CartItem } from '../../quickSale/types';
import type { KitchenOrderItem, KitchenOrderSubItem, KitchenPrintResult } from '../components/KitchenPrintService';
import type { NavigateFunction } from 'react-router-dom';

// ─────────────────────────────────────────────────────────
// 🛡️ MÜKERRER YAZDIRMA KORUMASI (parmak izi)
//
// Aynı masaya, aynı satır kümesi (sqlItemId + yazdırma-öncesi PrintedQuantity
// + yazdırılacak miktar) kısa süre içinde ikinci kez gönderilmeye çalışılırsa
// engellenir. Parmak izi localStorage'da tutulur, böylece masadan çıkıp geri
// girme (component remount) senaryosunda da koruma devam eder.
//
// printedQtyBefore parmak izine dahildir: garson gerçekten aynı üründen bir
// adet daha eklerse (örn. yazdırılmış 1 → miktar 2), yeni delta (id, 1, 1)
// ilk yazdırmanın (id, 0, 1) parmak iziyle ÇAKIŞMAZ — yanlış engelleme olmaz.
// ─────────────────────────────────────────────────────────
const KITCHEN_PRINT_GUARD_TTL_MS = 90_000; // 90 saniye

const buildKitchenPrintFingerprint = (
  tableId: string | null,
  entries: { sqlItemId?: number; productId: string; printedQtyBefore: number; qtyToPrint: number }[],
): string => {
  const parts = entries
    .map(e => `${e.sqlItemId ?? `p:${e.productId}`}|${e.printedQtyBefore}|${e.qtyToPrint}`)
    .sort();
  return `${tableId ?? 'no-table'}::${parts.join(';')}`;
};

const kitchenPrintGuardKey = (tableId: string | null) => `gts_kitchen_print_guard_${tableId ?? 'na'}`;

/** Son başarılı yazdırmanın parmak izini oku; TTL geçmişse null döner. */
const readKitchenPrintGuard = (tableId: string | null): { fp: string; at: number } | null => {
  try {
    const raw = localStorage.getItem(kitchenPrintGuardKey(tableId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fp || !parsed?.at) return null;
    if (Date.now() - parsed.at > KITCHEN_PRINT_GUARD_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeKitchenPrintGuard = (tableId: string | null, fingerprint: string): void => {
  try {
    localStorage.setItem(
      kitchenPrintGuardKey(tableId),
      JSON.stringify({ fp: fingerprint, at: Date.now() }),
    );
  } catch {
    // localStorage dolu/kapalı olabilir — koruma katmanlarından sadece biri, sessiz geç
  }
};

// Hook parametreleri için tip tanımı
export interface UseRestaurantKitchenOperationsParams {
  // Temel bilgiler
  isRestaurantMode: boolean;
  useKitchenScreen: boolean;
  tableId: string | null;
  tableName: string | null;
  restaurantScreenId: number | null;
  companyId: string | null;
  waiterName: string | null;
  
  // Sepet
  cart: CartItem[];
  clearCart: () => void;
  calculateTotal: () => number;
  
  // SQL veriler
  sqlInventoryItems: any[];
  allCategoriesData: any[];
  
  // Ekran ayarları
  screenSettings: any;
  currentScreen: any;
  quickSaleUserData: any;
  
  // Sipariş numaraları
  reservedLongCode: string | null;
  reservedShortCode: string | null;
  
  // Navigasyon
  navigate: NavigateFunction;
  
  // Snackbar
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  
  // State setters
  setIsClosingTable: React.Dispatch<React.SetStateAction<boolean>>;
  setUnprintedItemsList: React.Dispatch<React.SetStateAction<any[]>>;
  setCancelledItemsList: React.Dispatch<React.SetStateAction<any[]>>;
  setUnprintedItemsWarningOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsReceiptPrintedForCurrentTable: React.Dispatch<React.SetStateAction<boolean>>;
  showKitchenPrintInfoModal?: (
    printedItems: { name: string; quantity: number }[],
    unprintedItems: { name: string; quantity: number }[],
    /** ✅ YENİ: Kategorisinde yazıcı atanmadığı için yazdırılamayan ürünler */
    skippedItems?: { name: string; quantity: number; categoryName?: string }[],
  ) => void;
  /**
   * ✅ YENİ: Kategori bazlı yazdırmada, sepetteki TÜM ürünlerin kategorisine
   * yazıcı atanmamış olduğunda gösterilecek uyarı modalı.
   * Örn: tek bir ürün var, kategorisine yazıcı atanmamış → bu çağrılır.
   */
  showNoCategoryPrinterModal?: (
    skippedCategoryNames: string[],
    skippedItems: { name: string; quantity: number; categoryName?: string }[],
  ) => void;

  // State değerleri
  cancelledItemsList: any[];
  unprintedItemsList: any[];
  
  // Diğer fonksiyonlar
  addWaiterAction: (activeSaleId: string | number, actionType: string, details: any) => Promise<void>;
  printKitchenReceiptByCategory: (data: any) => Promise<KitchenPrintResult>;
  
  // İndirim bilgileri
  lastAppliedDiscountAmount?: number;
  appliedDiscountType?: 'percentage' | 'amount' | null;
  discountInputPercentage?: string;

  // Servisler
  salesService: any;
  
  // Sepet güncelleme (printedQuantity için)
  setCart?: React.Dispatch<React.SetStateAction<CartItem[]>>;

  /** Mutfağa yazdır (yeni kalemler) başarıyla tamamlandığında — örn. masalara dönüş geri sayımı */
  onAfterSuccessfulKitchenPrint?: () => void;

  /**
   * Adisyon (müşteri fişi) başarıyla yazdırıldığında tetiklenir — masalara
   * dönüş geri sayımı için. `onAfterSuccessfulKitchenPrint` ile aynı geri
   * dönüş davranışını sağlar; QuickSaleScreen aynı `scheduleRestaurantReturnAfterKitchenPrint`
   * fonksiyonunu buraya da geçer.
   */
  onAfterSuccessfulCheckPrint?: () => void;

  /** Seçili cari hesap adı (fişte gösterilmek üzere) */
  selectedCustomerName?: string | null;

  /**
   * ✅ YENİ: SADECE adisyon fişinde kullanılacak garson adı (override).
   * Restoran ekran ayarları "Adisyonda İlk Açan Garson" toggle'ı açıkken,
   * QuickSaleScreen tarafından masayı ilk açan garsonun adı buraya verilir.
   * Verilmezse fallback olarak `waiterName` kullanılır.
   * Mutfak yazdırma, iptal vb. diğer akışlar bu değerden ETKİLENMEZ.
   */
  receiptWaiterName?: string | null;
}

// Hook dönüş tipi
export interface UseRestaurantKitchenOperationsReturn {
  /**
   * Ana mutfağa yazdır fonksiyonu.
   * `options.skipCountdown=true` verilirse başarı sonrası geri sayım modalı
   * (`onAfterSuccessfulKitchenPrint`) tetiklenmez. Geri-dönüş modal
   * akışlarından çağrılırken kullanılır (zaten direkt navigate yapılır).
   */
  handlePrintToKitchen: (options?: { skipCountdown?: boolean }) => Promise<void>;
  handlePrintCheck: () => Promise<void>;
  handleBackButtonClick: () => Promise<void>;
  handleContinueWithoutPrinting: () => void;
  handlePrintToKitchenAndGoBack: () => Promise<void>;
  handlePrintCancellationAndGoBack: () => Promise<void>;
  handlePrintBothAndGoBack: () => Promise<void>;
  handleCloseTable: (cancellationReason?: string) => Promise<void>;
  /**
   * ✅ YENİ: Yazdırılmış bir ürünün silinmesi/azaltılması anında, ilgili
   * miktar için iptal fişi oluşturup mutfak yazıcılarına gönderir.
   * `handlePrintToKitchen`'ın aksine SQL state'iyle ilgilenmez (yani cart vs
   * existingItems karşılaştırması yapmaz, `markAsPrinted`'i çağırmaz, ilave
   * sipariş yazdırması yapmaz). Sadece verilen item ve `cancelledQuantity`
   * parametreleriyle iptal fişi oluşturur.
   *
   * Hata durumunda silme/azaltma akışını bloke etmez; sadece warning loglar.
   */
  printCancellationForRemovedItem: (params: {
    item: CartItem;
    cancelledQuantity: number;
    previousQuantity: number;
    currentQuantity: number;
  }) => Promise<void>;
}

/**
 * Restaurant modu için mutfak ve yazdırma işlemleri hook'u
 */
export const useRestaurantKitchenOperations = ({
  isRestaurantMode,
  useKitchenScreen,
  tableId,
  tableName,
  restaurantScreenId,
  companyId,
  waiterName,
  cart,
  clearCart,
  calculateTotal,
  sqlInventoryItems,
  allCategoriesData,
  screenSettings,
  currentScreen,
  quickSaleUserData,
  reservedLongCode,
  reservedShortCode,
  navigate,
  showSnackbar,
  setIsClosingTable,
  setUnprintedItemsList,
  setCancelledItemsList,
  setUnprintedItemsWarningOpen,
  setIsReceiptPrintedForCurrentTable,
  showKitchenPrintInfoModal,
  showNoCategoryPrinterModal,
  cancelledItemsList,
  unprintedItemsList,
  addWaiterAction,
  printKitchenReceiptByCategory,
  lastAppliedDiscountAmount,
  appliedDiscountType,
  discountInputPercentage,
  salesService,
  setCart,
  onAfterSuccessfulKitchenPrint,
  onAfterSuccessfulCheckPrint,
  selectedCustomerName,
  receiptWaiterName,
}: UseRestaurantKitchenOperationsParams): UseRestaurantKitchenOperationsReturn => {

  // 🛡️ Eşzamanlılık kilidi: handlePrintToKitchen çalışırken (SQL okuma →
  // mutfak API → yazıcı → markAsPrinted) ikinci bir tetikleme mükerrer fiş
  // üretir. Buton disabled olmadığı için çift tıklama / sabırsız tekrar basma
  // bu ref ile engellenir.
  const kitchenPrintInFlightRef = useRef(false);

  /**
   * Restaurant Modu - Adisyon İptal Fonksiyonu
   * "Adisyon İptal" butonuna basıldığında çağrılır
   */
  const handleCloseTable = useCallback(async (cancellationReason?: string) => {
    if (!isRestaurantMode || !restaurantScreenId || !tableId || !companyId) {
      console.error('[ADİSYON İPTAL] ❌ Gerekli bilgiler eksik:', { isRestaurantMode, restaurantScreenId, tableId, companyId });
      showSnackbar('Masa bilgisi bulunamadı.', 'error');
      return;
    }

    try {
      console.log('[ADİSYON İPTAL] 🍽️ Adisyon iptal ediliyor...', {
        restaurantScreenId,
        tableId,
        tableName,
        companyId,
        cartItems: cart.length
      });

      setIsClosingTable(true);

      const { restaurantActiveSaleApi, restaurantCompletedSaleApi, restaurantTableApi } = await import('../../../../services/restaurantApiService');

      // 1️⃣ Aktif satışı al
      console.log('[ADİSYON İPTAL] 📡 getByTableId çağrılıyor, tableId:', parseInt(tableId));
      const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
      console.log('[ADİSYON İPTAL] 📡 getByTableId yanıtı:', activeSale ? { Id: activeSale.Id, BranchId: activeSale.BranchId, TotalAmount: activeSale.TotalAmount, TableId: activeSale.TableId } : 'NULL');
      
      if (!activeSale) {
        showSnackbar('Bu masa için aktif satış bulunamadı.', 'warning');
        setIsClosingTable(false);
        return;
      }

      // 2️⃣ Masa bilgilerini al
      const table = await restaurantTableApi.getById(parseInt(tableId));

      // 3️⃣ Son waiter action'ı kaydet
      await addWaiterAction(activeSale.Id, 'cancel_check', {
        cartItems: cart.length,
        totalAmount: activeSale.TotalAmount,
        reason: 'Adisyon iptal edildi'
      });

      const updatedActiveSale = await restaurantActiveSaleApi.getById(activeSale.Id);

      // 4️⃣ RestaurantCompletedSales'e taşı - UNPAID olarak
      const createPayload = {
        CompanyId: companyId,
        BranchId: activeSale.BranchId || null,
        RestaurantScreenId: restaurantScreenId,
        TableId: parseInt(tableId),
        TableName: table.Name,
        TableGroupName: table.TableGroupName || null,
        SaleNumber: `IPTAL-${tableId}-${Date.now()}`,
        SubTotal: activeSale.SubTotal,
        DiscountAmount: activeSale.DiscountAmount,
        TaxAmount: activeSale.TaxAmount,
        TotalAmount: activeSale.TotalAmount,
        ItemsJson: JSON.stringify(activeSale.Items),
        PaymentMethod: 'UNPAID',
        PaymentDetailsJson: null,
        OpenedAt: activeSale.OpenedAt,
        OpenedBy: activeSale.OpenedBy,
        OpenedByName: waiterName || 'Bilinmeyen',
        CompletedBy: waiterName || 'Bilinmeyen',
        CompletedByName: waiterName || 'Bilinmeyen',
        Notes: `${activeSale.Notes || ''} [ADİSYON İPTAL EDİLDİ]${cancellationReason ? ` [İPTAL NEDENİ: ${cancellationReason}]` : ''}`.trim(),
        WaiterActionsJson: updatedActiveSale.WaiterActionsJson,
        CancellationReason: cancellationReason || null,
      };
      console.log('[ADİSYON İPTAL] 📤 restaurantCompletedSaleApi.create çağrılıyor...', createPayload);
      
      const createResult = await restaurantCompletedSaleApi.create(createPayload);
      console.log('[ADİSYON İPTAL] 📥 create yanıtı:', JSON.stringify(createResult));

      console.log('[ADİSYON İPTAL] ✅ RestaurantCompletedSales\'e UNPAID olarak taşındı!');

      // 5️⃣ RestaurantActiveSales'den sil
      await restaurantActiveSaleApi.delete(activeSale.Id);

      console.log('[ADİSYON İPTAL] ✅ RestaurantActiveSales\'den silindi!');
      
      showSnackbar(`${tableName} adisyonu iptal edildi!`, 'warning');
      
      clearCart();
      
      setTimeout(() => {
        navigate(`/restaurant-screen/${restaurantScreenId}`);
      }, 500);

    } catch (error: any) {
      console.error('[ADİSYON İPTAL] ❌ Hata:', error);
      console.error('[ADİSYON İPTAL] ❌ Hata detayı:', error?.response?.data || error?.message);
      showSnackbar(`Hata oluştu: ${(error as Error).message}`, 'error');
    } finally {
      setIsClosingTable(false);
    }
  }, [isRestaurantMode, restaurantScreenId, tableId, tableName, companyId, cart, waiterName, showSnackbar, setIsClosingTable, addWaiterAction, clearCart, navigate]);

  /**
   * ✅ YENİ: Yazdırılmış ürünün silinmesi/azaltılması anında otomatik tetiklenen
   * iptal fişi yazdırma fonksiyonu.
   *
   * `handlePrintToKitchen`'in aksine SQL state'i ile ilgilenmez (cart vs
   * existingItems diff yapmaz, `markAsPrinted`'i çağırmaz, ilave sipariş
   * yazdırmaz). Doğrudan verilen `item` + `cancelledQuantity` ile iptal fişi
   * oluşturup mutfak yazıcısına gönderir.
   *
   * Kullanım yeri: `useRestaurantCartOperations.removeFromCartWithRestaurant`
   * (SQL delete ÖNCESİ) ve `updateQuantityWithRestaurant` (SQL update SONRASI).
   *
   * Hata durumunda silme/azaltma akışını bloke etmez (sadece warning loglar).
   */
  const printCancellationForRemovedItem = useCallback(async (params: {
    item: CartItem;
    cancelledQuantity: number;
    previousQuantity: number;
    currentQuantity: number;
  }) => {
    const { item, cancelledQuantity, previousQuantity, currentQuantity } = params;

    if (!isRestaurantMode || !companyId) {
      console.warn('[İPTAL FİŞİ] Restoran modu/companyId eksik, iptal fişi atlandı.');
      return;
    }
    if (cancelledQuantity <= 0) {
      console.log('[İPTAL FİŞİ] cancelledQuantity 0 veya negatif, atlandı:', { product: item.name, cancelledQuantity });
      return;
    }

    const selectedQRMenuId = screenSettings?.selectedQRMenuId;
    if (!selectedQRMenuId) {
      console.warn('[İPTAL FİŞİ] QR Menü seçilmemiş — iptal fişi mutfağa gönderilemiyor.');
      return;
    }

    try {
      // Kategori-yazıcı eşlemesi (handlePrintToKitchen ile aynı mantık)
      const printingSettingsParsed = (() => {
        const raw = screenSettings?.PrintingSettingsJson;
        if (!raw) return null;
        try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
      })();
      const isCategoryMode = printingSettingsParsed?.printingMode === 'category';
      const categoryAssignmentsMap: Record<string, string | undefined> =
        printingSettingsParsed?.categoryPrinterAssignments || {};
      // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel)
      const categoryAssignmentsMap2: Record<string, string | undefined> =
        printingSettingsParsed?.categoryPrinterAssignments2 || {};

      // stockVisibility.products → productCategoryLookup (warehouse-bazlı)
      const productCategoryLookup: Record<string, string> = {};
      const allProductSettings = screenSettings?.stockVisibility?.products || {};
      for (const wId of Object.keys(allProductSettings)) {
        const wp = allProductSettings[wId];
        if (wp && typeof wp === 'object') {
          for (const cId of Object.keys(wp)) {
            const pIds = wp[cId];
            if (Array.isArray(pIds)) {
              for (const pid of pIds) {
                if (!productCategoryLookup[pid]) productCategoryLookup[pid] = cId;
              }
            }
          }
        }
      }

      const inv = sqlInventoryItems.find((x: any) => x.id === item.id);
      // 🔑 stockVisibility kategorisi ÖNCELİKLİ — categoryPrinterAssignments
      // bu kategori ID'lerine göre yapılır. Inventory ID son çare.
      const resolvedCategoryId = productCategoryLookup[item.id]
        || (item as any).categoryId
        || inv?.categoryId
        || undefined;

      // Kategori-bazlı modda yazıcı atanmamışsa sessizce skip
      // (kullanıcı kasten o kategoriye yazıcı atamamış olabilir).
      if (isCategoryMode && (!resolvedCategoryId || (!categoryAssignmentsMap[resolvedCategoryId] && !categoryAssignmentsMap2[resolvedCategoryId]))) {
        console.log('[İPTAL FİŞİ] Bu ürünün kategorisinde yazıcı atanmamış, iptal fişi atlandı:', {
          product: item.name,
          categoryId: resolvedCategoryId,
        });
        return;
      }

      const cancelSiparisDetaylari = [{
        UrunId: item.id || '',
        UrunAdi: item.name,
        BirimFiyat: 0,
        Miktar: cancelledQuantity,
        ToplamFiyat: 0,
        UrunResmi: null,
        Aciklama: `İPTAL: ${previousQuantity} → ${currentQuantity}`,
        CategoryId: resolvedCategoryId || null,
        VatRate: null,
      }];

      const cancelKitchenOrderRequest = {
        QRMenuId: selectedQRMenuId,
        PersonelFirebaseUid: quickSaleUserData?.uid || null,
        PersonelName: waiterName || 'Garson',
        ToplamTutar: 0,
        SiparisDetaylari: cancelSiparisDetaylari,
        SiparisNotu: `Masa: ${tableName} (İPTAL)`,
        OdemeYontemi: 'İptal',
        IslemId: null,
        DekontId: null,
        QuickSaleScreenId: currentScreen?.id ? parseInt(currentScreen.id) : null,
        ReferenceNumber: tableName,
        IsRestaurantMode: true,
        IsCancellationOrder: true,
        ReservedLongCode: reservedLongCode || null,
        ReservedShortCode: reservedShortCode || null,
        TableName: tableName || null,
        CustomerAccountName: selectedCustomerName || null,
      };

      console.log('🖨️ [İPTAL FİŞİ] Mutfak yazıcısına gönderiliyor:', {
        product: item.name,
        cancelledQuantity,
        previousQuantity,
        currentQuantity,
        categoryId: resolvedCategoryId,
      });

      const cancelResponse = await salesService.createKitchenOrderFromQuickSale(cancelKitchenOrderRequest);

      if (cancelResponse.success) {
        const cancelReceiptData = {
          orderNumber: reservedShortCode || `RS-${Date.now()}`,
          tableName: tableName || undefined,
          waiterName: waiterName || undefined,
          items: [{
            id: item.id || '',
            name: item.name,
            quantity: cancelledQuantity,
            note: `İptal: ${previousQuantity} → ${currentQuantity}`,
            categoryId: resolvedCategoryId,
          }] as KitchenOrderItem[],
          orderNote: 'İPTAL EDİLEN ÜRÜNLER',
          companyName: screenSettings?.receiptDisplaySettings?.companyName || undefined,
          isCancellationOrder: true,
        };

        await printKitchenReceiptByCategory(cancelReceiptData);
        showSnackbar(`İptal fişi mutfağa gönderildi: ${item.name} (${cancelledQuantity} adet)`, 'warning');
        console.log('✅ [İPTAL FİŞİ] Yazdırıldı');
      } else {
        console.warn('⚠️ [İPTAL FİŞİ] Mutfak ekranına gönderilemedi:', cancelResponse.message);
      }
    } catch (error: any) {
      // Hata durumunda silme/azaltma akışını BLOKE ETME — sadece logla.
      console.warn('⚠️ [İPTAL FİŞİ] İptal fişi gönderilemedi:', error?.message || error);
    }
  }, [isRestaurantMode, companyId, screenSettings, sqlInventoryItems, quickSaleUserData, waiterName, tableName, currentScreen, reservedLongCode, reservedShortCode, selectedCustomerName, salesService, printKitchenReceiptByCategory, showSnackbar]);

  /**
   * "Yine de Devam Et" butonuna tıklandığında
   */
  const handleContinueWithoutPrinting = useCallback(() => {
    console.log('🚶 [GERİ BUTONU] Kullanıcı yazdırmadan devam etmeyi seçti.');
    navigate(`/restaurant-screen/${restaurantScreenId}`);
  }, [navigate, restaurantScreenId]);

  /**
   * Restaurant Modu - Geri Butonu Kontrolü
   * Sepette yazdırılmamış (ilave) ürün varsa uyarı modalı gösterir.
   * NOT: İptal kontrolü kaldırıldı — iptal fişi silme/azaltma anında
   * otomatik basıldığı için geri butonunda iptal uyarısı gösterilmez.
   */
  const handleBackButtonClick = useCallback(async () => {
    if (!isRestaurantMode) {
      navigate(`/restaurant-screen/${restaurantScreenId}`);
      return;
    }

    try {
      console.log('🔙 [GERİ BUTONU] Yazdırılmamış (ilave) ürün kontrolü yapılıyor...', {
        cartLength: cart.length,
        tableId,
        restaurantScreenId
      });

      const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');
      
      let existingItems: any[] = [];
      
      if (tableId) {
        try {
          const numericTableId = parseInt(tableId);
          const activeSale = await restaurantActiveSaleApi.getByTableId(numericTableId);
          
          if (activeSale) {
            existingItems = activeSale.Items || [];
            console.log('📊 [GERİ BUTONU] Aktif satış bilgisi:', {
              activeSaleId: activeSale.Id,
              kitchenPrintCount: activeSale.KitchenPrintCount,
              existingItemCount: existingItems.length
            });
            
            // ✅ DEBUG: SQL'deki PrintedQuantity değerlerini göster
            console.log('📊 [GERİ BUTONU] SQL Items PrintedQuantity:', existingItems.map((i: any) => ({
              productId: i.ProductId,
              productName: i.ProductName,
              quantity: i.Quantity,
              printedQuantity: i.PrintedQuantity,
              rawItem: i
            })));
          }
        } catch (error) {
          console.warn('⚠️ [GERİ BUTONU] Aktif satış bilgisi alınamadı:', error);
        }
      }

      if (cart.length === 0 && existingItems.length === 0) {
        navigate(`/restaurant-screen/${restaurantScreenId}`);
        return;
      }

      const unprintedItems: any[] = [];

      // ─────────────────────────────────────────────────────────
      // İLAVE (yazdırılmamış) tespiti — SATIR (sqlItemId) BAZLI
      //
      // Her sepet satırı kendi sqlItemId'sindeki PrintedQuantity ile
      // karşılaştırılır; aradaki fark kadar "yazdırılmamış" sayılır.
      // Ürün bazlı tahsis YAPILMAZ (her satırın kendi notu olabilir).
      // ─────────────────────────────────────────────────────────
      for (const cartItem of cart) {
        const sqlItem = cartItem.sqlItemId
          ? existingItems.find((i: any) => String(i.Id) === String(cartItem.sqlItemId))
          : undefined;
        // 🛡️ SQL ve yerel printedQuantity'nin büyüğü esas alınır (markAsPrinted
        // SQL'e yazılamadıysa yerel değer mükerrer "yazdırılmamış" uyarısını
        // ve modal üzerinden ikinci kez yazdırmayı engeller).
        const printedQty = Math.max(
          sqlItem ? (sqlItem.PrintedQuantity || 0) : 0,
          cartItem.printedQuantity || 0,
        );
        const unprintedQty = cartItem.quantity - printedQty;
        if (unprintedQty > 0) {
          const inventoryItem = sqlInventoryItems.find((inv: any) => inv.id === cartItem.id);
          unprintedItems.push({
            id: cartItem.id,
            name: cartItem.name,
            price: cartItem.price || 0,
            vatRate: cartItem.vatRate,
            quantity: unprintedQty,
            printedQuantity: printedQty,
            unprintedQuantity: unprintedQty,
            categoryId: (cartItem as any).categoryId || inventoryItem?.categoryId || undefined,
            note: cartItem.note || undefined,
          });
        }
      }

      // ─────────────────────────────────────────────────────────
      // İPTAL TESPİTİ KALDIRILDI (bilinçli):
      // Ürün silme ve miktar azaltma akışları iptal fişini artık OTOMATİK
      // bastırıyor (useRestaurantCartOperations → printCancellationForRemovedItem)
      // ve PrintedQuantity'yi senkron tutuyor / SQL satırını siliyor.
      // Bu yüzden geri butonunda "yazdırılmayan iptal ürünler var" uyarısına
      // gerek kalmadı; modal iptal nedeniyle HİÇBİR ZAMAN açılmamalı.
      // İlave (hiç yazdırılmamış yeni ürün) uyarısı ise korunuyor.
      // ─────────────────────────────────────────────────────────

      // ─────────────────────────────────────────────────────────
      // KATEGORI-BAZLI YAZICISIZ URUN FILTRESI
      //
      // Eger ekran kategori-bazli yazdirma modunda ise, kategorisinde
      // yazici atanmamis urunler "yazdirilmamis" olarak SAYILMAMALI:
      // - Restoran isletmesi bazi urunlerin (orn. icecek, tatli) mutfaga
      //   yazdirilmasini istemez ve kasten o kategorilere yazici atamaz.
      // - Bu urunler eklendiginde kullanici geri dondukten sonra "yazdirilmamis
      //   urun var" uyarisini almamali (sessiz skip).
      //
      // Tek-yazici modunda filtre ETKISIZ (her sey ayni yaziciya gider).
      // ─────────────────────────────────────────────────────────
      const printingSettingsParsed = (() => {
        const raw = (screenSettings as any)?.PrintingSettingsJson;
        if (!raw) return null;
        try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
      })();
      const isCategoryMode = printingSettingsParsed?.printingMode === 'category';
      const categoryAssignmentsMap: Record<string, string | undefined> =
        printingSettingsParsed?.categoryPrinterAssignments || {};
      // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel)
      const categoryAssignmentsMap2: Record<string, string | undefined> =
        printingSettingsParsed?.categoryPrinterAssignments2 || {};

      // productCategoryLookup — handlePrintToKitchen'daki ile birebir ayni
      // (warehouse-bazli stockVisibility.products yapisi).
      const productCategoryLookup: Record<string, string> = {};
      const allProductSettings = screenSettings?.stockVisibility?.products || {};
      for (const wId of Object.keys(allProductSettings)) {
        const wp = allProductSettings[wId];
        if (wp && typeof wp === 'object') {
          for (const cId of Object.keys(wp)) {
            const pIds = wp[cId];
            if (Array.isArray(pIds)) {
              for (const pid of pIds) {
                if (!productCategoryLookup[pid]) productCategoryLookup[pid] = cId;
              }
            }
          }
        }
      }

      // 🔑 ÖNEMLİ: categoryPrinterAssignments, hızlı satış ekran ayarlarındaki
      // depo-stok tab'ında seçilen kategorilere (stockVisibility) göre atanır.
      // Bu yüzden ürünün Inventory tablosundaki orijinal kategorisi YANLIŞ
      // sonuç verebilir (ID'ler örtüşmeyebilir). Önce stockVisibility lookup,
      // SONRA Inventory fallback (son çare) kullanılır.
      const hasPrinterForProduct = (productId: string, fallbackCategoryId?: string): boolean => {
        if (!isCategoryMode) return true;
        const inv = sqlInventoryItems.find((x: any) => x.id === productId);
        const catId = productCategoryLookup[productId] || fallbackCategoryId || inv?.categoryId;
        if (!catId) return false;
        return !!categoryAssignmentsMap[catId] || !!categoryAssignmentsMap2[catId];
      };

      const filteredUnprinted = unprintedItems.filter((u: any) =>
        hasPrinterForProduct(u.id, u.categoryId)
      );

      const skippedCount = unprintedItems.length - filteredUnprinted.length;
      if (skippedCount > 0) {
        console.log(
          `[GERİ BUTONU] ${skippedCount} kalem yazicisiz kategoriye ait — filtrelendi (uyarida gorunmeyecek)`
        );
      }

      if (filteredUnprinted.length > 0) {
        console.log(`⚠️ [GERİ BUTONU] UYARI: ${filteredUnprinted.length} kalem ilave (yazdırılmamış) var!`);
        setUnprintedItemsList(filteredUnprinted);
        setCancelledItemsList([]);
        setUnprintedItemsWarningOpen(true);
        return;
      }

      console.log('✅ [GERİ BUTONU] Tüm ürünler yazdırılmış (veya yazicisiz kategoride), geri dönülüyor... (iptal kontrolü kaldırıldı — otomatik iptal fişi basılıyor)');
      navigate(`/restaurant-screen/${restaurantScreenId}`);

    } catch (error: any) {
      console.error('❌ [GERİ BUTONU] Kontrol hatası:', error);
      if (cart.length > 0) {
        const allUnprinted = cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price || 0,
          vatRate: item.vatRate,
          quantity: item.quantity,
          printedQuantity: 0,
          unprintedQuantity: item.quantity,
        }));
        setUnprintedItemsList(allUnprinted);
        setCancelledItemsList([]);
        setUnprintedItemsWarningOpen(true);
        return;
      }
      navigate(`/restaurant-screen/${restaurantScreenId}`);
    }
  }, [isRestaurantMode, cart, tableId, restaurantScreenId, navigate, setUnprintedItemsList, setCancelledItemsList, setUnprintedItemsWarningOpen, sqlInventoryItems, screenSettings]);

  /**
   * RESTAURANT MODU: Mutfağa Yazdır (İlave Sipariş Destekli)
   *
   * `options.skipCountdown=true` ise basari sonrasi
   * `onAfterSuccessfulKitchenPrint` (5sn'lik masa-donus geri sayim modali)
   * tetiklenmez. Geri-donus modal akislarindan (`handlePrintToKitchenAndGoBack`,
   * `handlePrintCancellationAndGoBack`, `handlePrintBothAndGoBack`)
   * cagrilirken bu opt-in kullanilir cunku zaten direkt `navigate` yapilir.
   */
  const handlePrintToKitchen = useCallback(async (
    options?: { skipCountdown?: boolean },
  ) => {
    if (!isRestaurantMode) {
      showSnackbar('Bu özellik sadece restoran modunda kullanılabilir.', 'warning');
      return;
    }

    if (cart.length === 0) {
      showSnackbar('Sepette ürün yok.', 'warning');
      return;
    }

    // 🛡️ ÖNLEM 1 — EŞZAMANLILIK KİLİDİ: Önceki yazdırma hâlâ sürüyorsa
    // (markAsPrinted dahil) ikinci tetiklemeyi engelle. Aksi halde ikinci
    // çağrı SQL'den henüz güncellenmemiş PrintedQuantity okur ve aynı fişi
    // ikinci kez basar.
    if (kitchenPrintInFlightRef.current) {
      console.warn('⛔ [MUTFAĞA YAZDIR] Yazdırma zaten devam ediyor — mükerrer tetikleme engellendi.');
      showSnackbar('Yazdırma devam ediyor, lütfen bekleyin...', 'warning');
      return;
    }
    kitchenPrintInFlightRef.current = true;

    try {
      console.log('🖨️ [MUTFAĞA YAZDIR] Mutfağa yazdırılıyor...', {
        restaurantScreenId,
        tableId,
        tableName,
        cartItems: cart.length
      });

      const selectedQRMenuId = screenSettings?.selectedQRMenuId;
      
      if (!selectedQRMenuId) {
        showSnackbar('Mutfak ekranı için QR Menü seçilmemiş! Ayarlardan QR Menü seçiniz.', 'error');
        return;
      }

      if (!companyId) {
        showSnackbar('Şirket ID bulunamadı!', 'error');
        return;
      }

      const { restaurantActiveSaleApi, restaurantActiveSaleItemApi } = await import('../../../../services/restaurantApiService');
      
      let activeSale: any = null;
      let existingItems: any[] = [];
      
      if (tableId) {
        try {
          const numericTableId = parseInt(tableId);
          activeSale = await restaurantActiveSaleApi.getByTableId(numericTableId);
          
          if (activeSale) {
            existingItems = activeSale.Items || [];
          }
        } catch (error) {
          // 🛡️ ÖNLEM 2 — SQL OKUNAMAZSA YAZDIRMA: Eskiden burada sadece warn
          // deyip devam ediliyordu; existingItems boş kaldığı için TÜM ürünler
          // "yazdırılmamış" sayılıp yeniden basılıyor ve activeSale null
          // olduğundan markAsPrinted de atlanıyordu (sessiz mükerrer fiş).
          // Artık yazdırılmışlık durumu doğrulanamıyorsa akış DURDURULUR.
          console.error('❌ [MUTFAĞA YAZDIR] Aktif satış bilgisi alınamadı — yazdırma iptal edildi:', error);
          showSnackbar('Masa bilgisi okunamadı, yazdırma iptal edildi. Lütfen tekrar deneyin.', 'error');
          return;
        }

        // 🛡️ ÖNLEM 2b — Sepette SQL'e kayıtlı satırlar (sqlItemId) varken aktif
        // satış bulunamadıysa (404) veri tutarsızlığı vardır; PrintedQuantity
        // doğrulanamayacağı için yazdırma yapılmaz.
        if (!activeSale && cart.some((c: CartItem) => c.sqlItemId)) {
          console.error('❌ [MUTFAĞA YAZDIR] Aktif satış bulunamadı ama sepette SQL satırları var — yazdırma iptal edildi.');
          showSnackbar('Masa satış kaydı doğrulanamadı, yazdırma iptal edildi. Masadan çıkıp tekrar girin.', 'error');
          return;
        }
      }

      const isAdditionalOrder = (activeSale?.KitchenPrintCount || 0) > 0;

      // Yazdırılacak ve iptal edilecek ürünleri hesapla
      const itemsToPrint: { item: CartItem; quantityToPrint: number; sqlItemId?: number; printedQtyBefore: number }[] = [];
      const itemsToCancelPrint: { name: string; productId: string; categoryId?: string; previousQuantity: number; currentQuantity: number; cancelledQuantity: number; sqlItemId?: number }[] = [];

      // ─────────────────────────────────────────────────────────
      // İLAVE tespiti — SATIR (sqlItemId) BAZLI
      //
      // Numpad her eklemede aynı ürün için AYRI bir SQL satırı açar; her
      // satırın kendi notu/seçeneği olabilir. Bu yüzden ürün bazlı gruplama
      // YAPILMAZ — her sepet satırı kendi sqlItemId'sindeki PrintedQuantity
      // ile karşılaştırılır. Eşleşme yoksa (yeni satır) tümü yazdırılır.
      //
      // 🛡️ ÖNLEM 3 — YEREL SAVUNMA: SQL'deki PrintedQuantity ile sepetteki
      // yerel printedQuantity'nin BÜYÜĞÜ esas alınır. markAsPrinted SQL'e
      // yazılamadıysa bile aynı oturumda yerel değer "bu satır yazdırıldı"
      // bilgisini taşır ve mükerrer basmayı engeller.
      // ─────────────────────────────────────────────────────────
      for (const cartItem of cart) {
        const sqlItem = cartItem.sqlItemId
          ? existingItems.find((i: any) => String(i.Id) === String(cartItem.sqlItemId))
          : undefined;
        const sqlPrintedQty = sqlItem ? (sqlItem.PrintedQuantity || 0) : 0;
        const localPrintedQty = cartItem.printedQuantity || 0;
        const printedQty = Math.max(sqlPrintedQty, localPrintedQty);
        const qtyToPrint = cartItem.quantity - printedQty;

        if (sqlPrintedQty !== localPrintedQty) {
          console.warn('⚠️ [MUTFAĞA YAZDIR] SQL/yerel PrintedQuantity uyuşmazlığı (büyük olan esas alındı):', {
            product: cartItem.name,
            sqlItemId: cartItem.sqlItemId,
            sqlPrintedQty,
            localPrintedQty,
          });
        }

        if (qtyToPrint > 0) {
          itemsToPrint.push({
            item: { ...cartItem, quantity: qtyToPrint },
            quantityToPrint: qtyToPrint,
            sqlItemId: cartItem.sqlItemId,
            printedQtyBefore: printedQty,
          });
        }
      }

      // ─────────────────────────────────────────────────────────
      // İPTAL tespiti — SATIR (sqlItemId) BAZLI
      //
      // Her SQL satırı, kendi sqlItemId'siyle eşleşen sepet satırının güncel
      // miktarıyla karşılaştırılır. Sepet miktarı PrintedQuantity'den azsa
      // aradaki fark kadar iptal fişi çıkar (fazla iptal yazılmaz).
      // ─────────────────────────────────────────────────────────
      for (const sqlItem of existingItems) {
        const printedQty = sqlItem.PrintedQuantity || 0;
        if (printedQty <= 0) continue;

        const productId = String(sqlItem.ProductId);
        const productName = sqlItem.ProductName || `Ürün #${productId}`;
        const cartRow = cart.find((c: CartItem) => String(c.sqlItemId) === String(sqlItem.Id));
        const currentQty = cartRow?.quantity ?? 0;

        if (currentQty < printedQty) {
          const cancelledQty = printedQty - currentQty;
          const inventoryItem = sqlInventoryItems.find((inv: any) => inv.id === productId);
          itemsToCancelPrint.push({
            name: productName,
            productId: productId,
            categoryId: inventoryItem?.categoryId || undefined,
            previousQuantity: printedQty,
            currentQuantity: currentQty,
            cancelledQuantity: cancelledQty,
            sqlItemId: sqlItem.Id,
          });
        }
      }

      const hasItemsToPrint = itemsToPrint.length > 0;
      const hasItemsToCancel = itemsToCancelPrint.length > 0;

      if (!hasItemsToPrint && !hasItemsToCancel) {
        showSnackbar('Tüm ürünler zaten mutfağa yazdırılmış!', 'info');
        return;
      }

      // İPTAL FİŞİ YAZDIRMA
      if (hasItemsToCancel) {
        const cancelSiparisDetaylari = itemsToCancelPrint.map(item => ({
          UrunId: item.productId || '',
          UrunAdi: item.name,
          BirimFiyat: 0,
          Miktar: item.cancelledQuantity,
          ToplamFiyat: 0,
          UrunResmi: null,
          Aciklama: `İPTAL: ${item.previousQuantity} → ${item.currentQuantity}`,
          CategoryId: item.categoryId || null,
          VatRate: null
        }));

        const cancelKitchenOrderRequest = {
  
          QRMenuId: selectedQRMenuId,
          PersonelFirebaseUid: quickSaleUserData?.uid || null,
          PersonelName: waiterName || 'Garson',
          ToplamTutar: 0,
          SiparisDetaylari: cancelSiparisDetaylari,
          SiparisNotu: `Masa: ${tableName} (İPTAL)`,
          OdemeYontemi: 'İptal',
          IslemId: null,
          DekontId: null,
          QuickSaleScreenId: currentScreen?.id ? parseInt(currentScreen.id) : null,
          ReferenceNumber: tableName,
          IsRestaurantMode: true,
          IsCancellationOrder: true,
          ReservedLongCode: reservedLongCode || null,
          ReservedShortCode: reservedShortCode || null,
          TableName: tableName || null,
          CustomerAccountName: selectedCustomerName || null
        };

        try {
          const cancelResponse = await salesService.createKitchenOrderFromQuickSale(cancelKitchenOrderRequest);
          
          if (cancelResponse.success) {
            const cancelReceiptData = {
              orderNumber: reservedShortCode || `RS-${Date.now()}`,
              tableName: tableName || undefined,
              waiterName: waiterName || undefined,
              items: itemsToCancelPrint.map(item => ({
                id: item.productId || '',
                name: item.name,
                quantity: item.cancelledQuantity,
                note: `İptal: ${item.previousQuantity} → ${item.currentQuantity}`,
                categoryId: item.categoryId,
              })),
              orderNote: 'İPTAL EDİLEN ÜRÜNLER',
              companyName: screenSettings?.receiptDisplaySettings?.companyName || undefined,
              isCancellationOrder: true,
            };

            await printKitchenReceiptByCategory(cancelReceiptData);
            showSnackbar(`${tableName} - İptal fişi mutfağa gönderildi! (${itemsToCancelPrint.length} kalem)`, 'warning');

            // İptal başarılı: SQL PrintedQuantity'yi düşür
            try {
              for (const cancelledItem of itemsToCancelPrint) {
                if (cancelledItem.sqlItemId) {
                  console.log(`📝 [İPTAL PrintedQuantity] ${cancelledItem.name}: PrintedQuantity → ${cancelledItem.currentQuantity}`);
                  await restaurantActiveSaleItemApi.update(cancelledItem.sqlItemId, {
                    PrintedQuantity: cancelledItem.currentQuantity
                  });
                }
              }
              console.log('✅ [MUTFAĞA YAZDIR] İptal sonrası PrintedQuantity güncellendi');
            } catch (pqError) {
              console.warn('⚠️ [MUTFAĞA YAZDIR] İptal PrintedQuantity güncellemesi başarısız:', pqError);
            }
          }
        } catch (cancelError: any) {
          console.warn('⚠️ [MUTFAĞA YAZDIR] İptal fişi gönderilemedi:', cancelError.message);
        }
      }

      if (!hasItemsToPrint) {
        return;
      }

      // 🛡️ ÖNLEM 4 — PARMAK İZİ (SON SAVUNMA): Aynı satır kümesi (sqlItemId +
      // yazdırma-öncesi PrintedQuantity + miktar) son 90 saniye içinde zaten
      // yazdırıldıysa engelle. Bu katman, masadan çıkıp geri girme (remount)
      // gibi eşzamanlılık kilidinin sıfırlandığı senaryoları da kapsar.
      // Meşru ilaveler engellenmez: yeni eklenen ürün yeni sqlItemId üretir,
      // mevcut satıra ekleme ise printedQtyBefore'u değiştirir → parmak izi
      // farklılaşır.
      const printFingerprint = buildKitchenPrintFingerprint(
        tableId,
        itemsToPrint.map(p => ({
          sqlItemId: p.sqlItemId,
          productId: p.item.id,
          printedQtyBefore: p.printedQtyBefore,
          qtyToPrint: p.quantityToPrint,
        })),
      );
      const lastPrintGuard = readKitchenPrintGuard(tableId);
      if (lastPrintGuard && lastPrintGuard.fp === printFingerprint) {
        const secondsAgo = Math.round((Date.now() - lastPrintGuard.at) / 1000);
        console.error('⛔ [MUTFAĞA YAZDIR] MÜKERRER YAZDIRMA ENGELLENDİ — aynı fiş az önce basıldı:', {
          secondsAgo,
          fingerprint: printFingerprint,
        });
        showSnackbar(
          `Bu fiş ${secondsAgo} saniye önce zaten mutfağa yazdırıldı! Mükerrer yazdırma engellendi.`,
          'error'
        );
        return;
      }

      // ✅ YENİ: Yazdırma öncesi PRE-COMPUTE — kategori bazlı modda hangi ürünler
      // skip edilecek (kategorisinde yazıcı atanmamış)? Buna göre:
      //  • TÜMÜ skip → uyarı modalı göster ve TÜM AKIŞI DURDUR
      //    (kitchen API'ye gitme, markAsPrinted yapma — kullanıcı yazıcı
      //     atadıktan sonra tekrar deneyecek)
      //  • KISMI skip → mevcut info modal'a 3. bölüm olarak ekle, normal akış devam
      const printingSettingsParsed = (() => {
        const raw = screenSettings?.PrintingSettingsJson;
        if (!raw) return null;
        try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
      })();
      const isCategoryMode = printingSettingsParsed?.printingMode === 'category';
      const categoryAssignmentsMap: Record<string, string | undefined> = printingSettingsParsed?.categoryPrinterAssignments || {};
      // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel)
      const categoryAssignmentsMap2: Record<string, string | undefined> = printingSettingsParsed?.categoryPrinterAssignments2 || {};

      // productCategoryLookup'ı erken hesapla (skip kontrolü için lazım)
      const earlyProductCategoryLookup: { [productId: string]: string } = {};
      const earlyAllProductSettings = screenSettings?.stockVisibility?.products || {};
      for (const wId of Object.keys(earlyAllProductSettings)) {
        const wp = earlyAllProductSettings[wId];
        if (wp && typeof wp === 'object') {
          for (const cId of Object.keys(wp)) {
            const pIds = wp[cId];
            if (Array.isArray(pIds)) {
              for (const pid of pIds) {
                if (!earlyProductCategoryLookup[pid]) earlyProductCategoryLookup[pid] = cId;
              }
            }
          }
        }
      }

      const skippedItemsPreview: { name: string; quantity: number; categoryName?: string; categoryId?: string }[] = [];
      if (isCategoryMode) {
        for (const { item, quantityToPrint } of itemsToPrint) {
          const inv = sqlInventoryItems.find((x: any) => x.id === item.id);
          // 🔑 stockVisibility kategorisi ÖNCELİKLİ — Inventory ID son çare
          // (categoryPrinterAssignments stockVisibility kategorilerine atanır).
          const catId = earlyProductCategoryLookup[item.id]
            || (item as any).categoryId
            || inv?.categoryId
            || undefined;
          const printerId = catId ? (categoryAssignmentsMap[catId] || categoryAssignmentsMap2[catId]) : undefined;
          if (!printerId) {
            const catName = catId
              ? allCategoriesData.find((c: any) => c.id === catId)?.name
              : undefined;
            skippedItemsPreview.push({
              name: item.name,
              quantity: quantityToPrint,
              categoryName: catName,
              categoryId: catId,
            });
          }
        }
      }

      const allSkipped = isCategoryMode && skippedItemsPreview.length > 0
        && skippedItemsPreview.length === itemsToPrint.length;

      if (allSkipped) {
        // 🚫 Tek bir ürün var (veya hepsinin kategorisinde yazıcı yok) → uyarı modalı
        const cats = Array.from(new Set(
          skippedItemsPreview.map(i => i.categoryName || '(Kategorisi olmayan)')
        ));
        console.warn(
          '🚫 [MUTFAĞA YAZDIR] Tüm ürünlerin kategorisinde yazıcı atanmamış — yazdırma iptal:',
          { cats, skippedItemsPreview }
        );
        if (showNoCategoryPrinterModal) {
          showNoCategoryPrinterModal(cats, skippedItemsPreview);
        } else {
          showSnackbar(
            `Bu kategori${cats.length > 1 ? 'lere' : 'ye'} yazıcı atanmamış: ${cats.join(', ')}`,
            'warning'
          );
        }
        return; // 🛑 Tüm akışı durdur (kitchen API, markAsPrinted YAPMA)
      }

      // Bilgi modalını göster: yazdırılmış + yazdırılacak + (varsa) atlanan
      if (showKitchenPrintInfoModal) {
        const alreadyPrinted: { name: string; quantity: number }[] = [];
        // Satır bazlı: her sepet satırının kendi sqlItemId'sindeki PrintedQuantity
        // (🛡️ SQL/yerel değerin büyüğü — ilave tespitiyle tutarlı)
        for (const cartItem of cart) {
          const sqlItem = cartItem.sqlItemId
            ? existingItems.find((i: any) => String(i.Id) === String(cartItem.sqlItemId))
            : undefined;
          const printedQty = Math.max(
            sqlItem ? (sqlItem.PrintedQuantity || 0) : 0,
            cartItem.printedQuantity || 0,
          );
          if (printedQty > 0) {
            alreadyPrinted.push({ name: cartItem.name, quantity: printedQty });
          }
        }
        // Skip olanları "şimdi yazdırılıyor"dan çıkar
        const skippedNameSet = new Set(skippedItemsPreview.map(s => s.name));
        const nowPrinting = itemsToPrint
          .filter(({ item }) => !skippedNameSet.has(item.name))
          .map(({ item, quantityToPrint }) => ({
            name: item.name,
            quantity: quantityToPrint,
          }));
        showKitchenPrintInfoModal(
          alreadyPrinted,
          nowPrinting,
          skippedItemsPreview.length > 0 ? skippedItemsPreview : undefined,
        );
      }

      // İLAVE SİPARİŞ YAZDIRMA
      const siparisDetaylari = itemsToPrint.map(({ item, quantityToPrint }) => {
        const inventoryItem = sqlInventoryItems.find((inv: any) => inv.id === item.id);
        return {
          UrunId: item.id,
          UrunAdi: item.name,
          BirimFiyat: item.price,
          Miktar: quantityToPrint,
          ToplamFiyat: item.price * quantityToPrint,
          UrunResmi: inventoryItem?.imageUrl || null,
          Aciklama: item.note || null,
          CategoryId: inventoryItem?.categoryId || null,
          VatRate: inventoryItem?.vatRate || null
        };
      });

      const kitchenOrderRequest = {

        QRMenuId: selectedQRMenuId,
        PersonelFirebaseUid: quickSaleUserData?.uid || null,
        PersonelName: waiterName || 'Garson',
        ToplamTutar: siparisDetaylari.reduce((sum, item) => sum + item.ToplamFiyat, 0),
        SiparisDetaylari: siparisDetaylari,
        SiparisNotu: `Masa: ${tableName}${isAdditionalOrder ? ' (İLAVE)' : ''}`,
        OdemeYontemi: 'Beklemede',
        IslemId: null,
        DekontId: null,
        QuickSaleScreenId: currentScreen?.id ? parseInt(currentScreen.id) : null,
        ReferenceNumber: tableName,
        IsRestaurantMode: true,
        ReservedLongCode: reservedLongCode || null,
        ReservedShortCode: reservedShortCode || null,
        TableName: tableName || null,
        CustomerAccountName: selectedCustomerName || null
      };

      const kitchenResponse = await salesService.createKitchenOrderFromQuickSale(kitchenOrderRequest);

      if (kitchenResponse.success) {
        // 🛡️ Parmak izini HEMEN kaydet (fiziksel yazdırma ve markAsPrinted'ten
        // önce): sipariş mutfak sistemine girdi, bu delta artık "basıldı"
        // sayılır. markAsPrinted başarısız olsa bile 90 sn boyunca aynı delta
        // ikinci kez yazdırılamaz.
        writeKitchenPrintGuard(tableId, printFingerprint);

        try {
          const productCategoryLookup: { [productId: string]: string } = {};
          const allProductSettings = screenSettings?.stockVisibility?.products || {};
          for (const warehouseId of Object.keys(allProductSettings)) {
            const warehouseProducts = allProductSettings[warehouseId];
            if (warehouseProducts && typeof warehouseProducts === 'object') {
              for (const catId of Object.keys(warehouseProducts)) {
                const productIds = warehouseProducts[catId];
                if (Array.isArray(productIds)) {
                  for (const pId of productIds) {
                    if (!productCategoryLookup[pId]) {
                      productCategoryLookup[pId] = catId;
                    }
                  }
                }
              }
            }
          }
          console.log('[KitchenOps] productCategoryLookup:', JSON.stringify(productCategoryLookup));

          // ─────────────────────────────────────────────────────────
          // Mutfak fişi — SATIR BAZLI (itemsToPrint doğrudan kullanılır)
          //
          // Tahsis (allocation) mantığı kaldırıldı: her itemsToPrint girişi
          // zaten tek bir sepet satırını (kendi notu/seçeneğiyle) ve o satıra
          // ait yazdırılacak miktarı (quantityToPrint) taşır. Böylece "ne
          // yazıldıysa o" — fazla/eksik adet çıkmaz.
          // ─────────────────────────────────────────────────────────
          const kitchenOrderItems: KitchenOrderItem[] = [];

          for (const { item, quantityToPrint } of itemsToPrint) {
            const qty = quantityToPrint;

            const inventoryItem = sqlInventoryItems.find((inv: any) => inv.id === item.id);
            const categoryName = inventoryItem?.categoryId 
              ? allCategoriesData.find((cat: any) => cat.id === inventoryItem.categoryId)?.name 
              : undefined;

            const subItems: KitchenOrderSubItem[] = [];

            if ((item as any).menuNormalItems && (item as any).menuNormalItems.length > 0) {
              (item as any).menuNormalItems.forEach((normalItem: any) => {
                subItems.push({
                  id: normalItem.itemId,
                  name: normalItem.itemName,
                  quantity: normalItem.quantity * qty,
                  isExtraOption: false,
                });
              });
            }

            if ((item as any).selectedOptions && (item as any).selectedOptions.length > 0) {
              (item as any).selectedOptions.forEach((option: any) => {
                if (option.selectedExtraOption) {
                  subItems.push({
                    id: option.selectedExtraOption.optionItemId,
                    name: `${option.selectedExtraOption.optionItemName} (+${option.selectedExtraOption.priceDifference.toFixed(2)} TL)`,
                    quantity: option.quantity * qty,
                    isExtraOption: true,
                  });
                } else {
                  subItems.push({
                    id: option.itemId,
                    name: option.itemName,
                    quantity: option.quantity * qty,
                    isExtraOption: false,
                  });
                }
              });
            }

            // 🔑 stockVisibility kategorisi ÖNCELİKLİ — kategori bazlı yazıcı
            // ataması (categoryPrinterAssignments) bu ID'lere göre yapılır.
            const resolvedCategoryId = productCategoryLookup[item.id] || (item as any).categoryId || undefined;

            kitchenOrderItems.push({
              id: item.id,
              name: item.name,
              quantity: qty,
              note: item.note || undefined,
              categoryName,
              categoryId: resolvedCategoryId,
              subItems: subItems.length > 0 ? subItems : undefined,
            });
          }

          const orderNumberForReceipt = reservedShortCode || kitchenResponse.data?.shortCode || `RS-${Date.now()}`;

          const kitchenReceiptData = {
            orderNumber: orderNumberForReceipt,
            tableName: tableName || undefined,
            waiterName: waiterName || undefined,
            items: kitchenOrderItems,
            orderNote: undefined,
            companyName: screenSettings?.receiptDisplaySettings?.companyName || undefined,
            isAdditionalOrder: isAdditionalOrder,
          };

          await printKitchenReceiptByCategory(kitchenReceiptData);
        } catch (printError: any) {
          console.warn('⚠️ [MUTFAĞA YAZDIR] Mutfak fişi yazdırılamadı:', printError.message);
        }

        // ─────────────────────────────────────────────────────────
        // "Yazdırıldı" işaretleme — SATIR (sqlItemId) BAZLI (EN KRİTİK)
        //
        // ESKİ HATA: aynı ürünün tüm sepet satırlarının miktarı, bulunan İLK
        // SQL satırının PrintedQuantity'sine yığılıyordu (örn. 1 adetlik bir
        // satıra PrintedQuantity=2 yazılması). Bu bozuk değer reload sonrası
        // ilave/iptal hesaplarına sızıp "fazla iptal / fazla ilave" sonucu
        // doğuruyordu.
        //
        // ARTIK: her sepet satırı KENDİ sqlItemId'sine KENDİ miktarını yazar.
        // Yığma yok → "ne yazıldıysa o".
        // ─────────────────────────────────────────────────────────
        if (activeSale) {
          const printedItems = cart
            .filter((c: CartItem) => c.sqlItemId)
            .map((c: CartItem) => ({
              ItemId: c.sqlItemId as number,
              PrintedQuantity: c.quantity,
            }));

          if (printedItems.length > 0) {
            // 🛡️ ÖNLEM 5 — markAsPrinted RETRY + GÖRÜNÜR HATA: Eskiden tek
            // deneme + console.warn idi; SQL'e yazılamazsa kimse fark etmiyor
            // ve bir sonraki basış mükerrer fiş üretiyordu. Artık 3 deneme
            // yapılır, yine olmazsa kullanıcıya belirgin hata gösterilir.
            let markSucceeded = false;
            for (let attempt = 1; attempt <= 3 && !markSucceeded; attempt++) {
              try {
                await restaurantActiveSaleItemApi.markAsPrinted({
                  ActiveSaleId: activeSale.Id,
                  PrintedItems: printedItems
                });
                markSucceeded = true;
                console.log(`✅ [MUTFAĞA YAZDIR] Ürünler yazdırıldı olarak işaretlendi (satır bazlı, deneme ${attempt})`, printedItems);
              } catch (markError) {
                console.warn(`⚠️ [MUTFAĞA YAZDIR] markAsPrinted deneme ${attempt}/3 başarısız:`, markError);
                if (attempt < 3) {
                  await new Promise(resolve => setTimeout(resolve, 700 * attempt));
                }
              }
            }

            if (!markSucceeded) {
              console.error('❌ [MUTFAĞA YAZDIR] KRİTİK: Yazdırıldı bilgisi SQL\'e KAYDEDİLEMEDİ (3 deneme)!');
              showSnackbar(
                'DİKKAT: Fiş yazdırıldı ama "yazdırıldı" bilgisi kaydedilemedi! Tekrar YAZDIRMAYIN — masadan çıkıp girin ve durumu kontrol edin.',
                'error'
              );
            }

            // Local cart'taki printedQuantity'yi HER DURUMDA güncelle (satır
            // bazlı). markAsPrinted başarısız olsa bile yerel değer, aynı
            // oturumdaki bir sonraki yazdırmada Math.max savunmasıyla
            // mükerrer basmayı engeller.
            if (setCart) {
              setCart(prevCart => prevCart.map(cartItem => {
                if (cartItem.sqlItemId) {
                  return { ...cartItem, printedQuantity: cartItem.quantity };
                }
                return cartItem;
              }));
            }
          }
        }
        
        const message = isAdditionalOrder 
          ? `${tableName} - İlave sipariş mutfağa gönderildi! (${itemsToPrint.length} kalem)`
          : `${tableName} mutfağa gönderildi! (${itemsToPrint.length} kalem)`;
        showSnackbar(message, 'success');
        // Geri-donus modal akislarinda (`skipCountdown=true`) 5sn'lik
        // masa-donus geri sayim modali atlanir; aksi halde flash gorunur.
        if (!options?.skipCountdown) {
          onAfterSuccessfulKitchenPrint?.();
        }
      } else {
        showSnackbar(`Mutfak ekranına gönderilemedi: ${kitchenResponse.message}`, 'error');
      }

    } catch (error: any) {
      console.error('❌ [MUTFAĞA YAZDIR] Hata:', error);
      showSnackbar(`Hata oluştu: ${(error as Error).message}`, 'error');
    } finally {
      // 🛡️ Eşzamanlılık kilidini her koşulda serbest bırak
      kitchenPrintInFlightRef.current = false;
    }
  }, [isRestaurantMode, cart, tableId, tableName, restaurantScreenId, companyId, waiterName, screenSettings, currentScreen, quickSaleUserData, reservedLongCode, reservedShortCode, sqlInventoryItems, allCategoriesData, salesService, printKitchenReceiptByCategory, showSnackbar, setCart, onAfterSuccessfulKitchenPrint]);

  // ─────────────────────────────────────────────────────────
  // GERI-DONUS MODAL CALLBACK'LERI — DELEGASYON
  //
  // `UnprintedItemsWarningModal`'in uc butonu da (Mutfaga Yazdir / Iptal /
  // Her Ikisi) ana `handlePrintToKitchen` fonksiyonunu cagirir. Ana fonksiyon
  // ZATEN su islemleri YAPAR:
  //   - Cart vs SQL diff ile ilave + iptal kalemlerini birlikte tespit
  //   - Kategori-bazli yazdirma + pre-compute skip kontrolu
  //   - "Kategoride yazici yok" uyari modali (gerekirse)
  //   - Yazdirma bilgi modali (yazdirilan/yazdirilacak/atlanan)
  //   - menuNormalItems + selectedOptions (modifier) ile zengin subItems
  //   - productCategoryLookup (warehouse-bazli) kategori fallback chain
  //   - Mutfak ekranina createKitchenOrderFromQuickSale + markAsPrinted
  //   - Iptal fisi mutfaga + SQL PrintedQuantity guncelle
  //   - Local cart printedQuantity guncelle + snackbar bildir
  //
  // Gecmisteki kopya-yapistir 3 fonksiyon (~500 satir) bu zenginligi
  // kacirdigi icin user "yazdirmiyor" diyordu. Delegasyon ile parite:
  //   - drift onlenir, tek dogruluk kaynagi
  //   - kullanicinin masa-donus geri sayim modali ({ skipCountdown: true })
  //     bu akista flash etmez; navigate hemen olur
  //
  // NOT: Modal'in 3 farkli butonu (Sadece Ilave / Sadece Iptal / Her Ikisi)
  // ayni davranisi tetikler — ana fonksiyon zaten ilave + iptal kalemlerini
  // ayni cagride birlikte isler. Bu kullanici tarafindan onaylandi (mutfak
  // iptalden de haberdar olmali).
  // ─────────────────────────────────────────────────────────

  /**
   * Mutfaga Yazdir ve Geri Don — Modal'dan cagrilir (Ilave urunler icin)
   */
  const handlePrintToKitchenAndGoBack = useCallback(async () => {
    console.log('[GERI BUTONU] handlePrintToKitchen delegasyonu (skipCountdown=true)...');
    await handlePrintToKitchen({ skipCountdown: true });
    setUnprintedItemsList([]);
    setCancelledItemsList([]);
    navigate(`/restaurant-screen/${restaurantScreenId}`);
  }, [handlePrintToKitchen, navigate, restaurantScreenId, setUnprintedItemsList, setCancelledItemsList]);

  /**
   * Iptal Fisi Yazdir ve Geri Don — Modal'dan cagrilir (Iptal urunler icin)
   *
   * Ana fonksiyon iptal kalemlerini de zaten isler.
   */
  const handlePrintCancellationAndGoBack = useCallback(async () => {
    console.log('[GERI BUTONU] handlePrintToKitchen delegasyonu (iptal, skipCountdown=true)...');
    await handlePrintToKitchen({ skipCountdown: true });
    setUnprintedItemsList([]);
    setCancelledItemsList([]);
    navigate(`/restaurant-screen/${restaurantScreenId}`);
  }, [handlePrintToKitchen, navigate, restaurantScreenId, setUnprintedItemsList, setCancelledItemsList]);

  /**
   * Hem Ilave hem Iptal Yazdir ve Geri Don — Modal'dan cagrilir
   */
  const handlePrintBothAndGoBack = useCallback(async () => {
    console.log('[GERI BUTONU] handlePrintToKitchen delegasyonu (her ikisi, skipCountdown=true)...');
    await handlePrintToKitchen({ skipCountdown: true });
    setUnprintedItemsList([]);
    setCancelledItemsList([]);
    navigate(`/restaurant-screen/${restaurantScreenId}`);
  }, [handlePrintToKitchen, navigate, restaurantScreenId, setUnprintedItemsList, setCancelledItemsList]);

  /**
   * RESTAURANT MODU - Adisyon Yazdır (Müşteri Fişi)
   */
  const handlePrintCheck = useCallback(async () => {
    if (!isRestaurantMode) {
      showSnackbar('Bu fonksiyon sadece restoran modunda kullanılabilir.', 'warning');
      return;
    }

    if (cart.length === 0) {
      showSnackbar('Sepette ürün yok.', 'warning');
      return;
    }

    if (!tableId) {
      showSnackbar('Masa bilgisi bulunamadı.', 'error');
      return;
    }

    try {
      console.log('🧾 [ADİSYON YAZDIR] Adisyon yazdırılıyor...', {
        restaurantScreenId,
        tableId,
        tableName,
        cartItems: cart.length,
        total: calculateTotal(),
        lastAppliedDiscountAmount,
        appliedDiscountType,
        discountInputPercentage,
        showDiscountInfo: screenSettings?.receiptDisplaySettings?.showDiscountInfo,
      });

      const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');
      const { printAdisyonReceipt } = await import('../components/AdisyonPrintService');

      const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
      if (!activeSale) {
        showSnackbar('Bu masa için aktif satış bulunamadı.', 'error');
        return;
      }

      const receiptItems = cart.map(item => ({
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.price,
        totalPrice: item.price * item.quantity,
        vatRate: item.vatRate || 18,
      }));

      const grandTotal = calculateTotal();

      // ✅ Fiş ayarlarını al (logo dahil)
      const receiptDisplaySettings = screenSettings?.receiptDisplaySettings;
      
      const discountAmt = lastAppliedDiscountAmount || 0;
      const discountPct = appliedDiscountType === 'percentage' && discountInputPercentage ? parseFloat(discountInputPercentage) : undefined;

      console.log('🏷️ [ADİSYON İNDİRİM] discountAmt:', discountAmt, '| showDiscountInfo ayarı:', receiptDisplaySettings?.showDiscountInfo, '| appliedDiscountType:', appliedDiscountType, '| discountPct:', discountPct, '| grandTotal:', grandTotal);

      // ✅ YENİ: "Adisyonda İlk Açan Garson" toggle'ı açıkken receiptWaiterName dolu gelir.
      // Diğer akışlar (mutfak yazdır, iptal vb.) bu değerden ETKİLENMEZ.
      const effectiveReceiptWaiterName = receiptWaiterName || waiterName || undefined;
      console.log('🧾 [ADİSYON YAZDIR] Garson adı seçimi:', {
        receiptWaiterName,
        loggedInWaiterName: waiterName,
        effectiveReceiptWaiterName,
      });

      const adisyonData = {
        invoiceNumber: `ADİSYON-${activeSale.ReservedShortCode || activeSale.Id}`,
        date: new Date().toLocaleDateString('tr-TR'),
        time: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
        tableName: tableName || 'Masa',
        items: receiptItems,
        subtotal: grandTotal + discountAmt,
        vatTotal: 0,
        grandTotal: grandTotal,
        waiterName: effectiveReceiptWaiterName,
        orderNumber: activeSale.ReservedShortCode || undefined,
        companyInfo: {
          name: receiptDisplaySettings?.companyName || '',
          address: receiptDisplaySettings?.companyAddress || '',
          taxNumber: receiptDisplaySettings?.taxNumber || '',
        },
        standardFooterNote: receiptDisplaySettings?.standardFooterNote || '',
        thankYouMessage: receiptDisplaySettings?.thankYouMessage || 'Bizi tercih ettiğiniz için teşekkür ederiz!',
        headerType: receiptDisplaySettings?.headerType || 'text',
        logoUrl: receiptDisplaySettings?.logoUrl || '',
        showDiscountInfo: !!receiptDisplaySettings?.showDiscountInfo && discountAmt > 0,
        discountAmount: discountAmt,
        discountTotal: (!!receiptDisplaySettings?.showDiscountInfo && discountAmt > 0) ? discountAmt : 0,
        discountType: appliedDiscountType || null,
        discountPercentage: discountPct,
      };

      try {
        // ✅ YENİ: Yazdırma moduna göre yazıcı seç
        // - Tek yazıcı modu: defaultReceiptPrinter kullan
        // - Kategori bazlı mod: adisyonPrinter kullan (restoran için özel)
        const printingMode = (screenSettings as any)?.printingMode || 'category';
        const defaultPrinter = (screenSettings as any)?.defaultReceiptPrinter;
        const adisyonPrinter = (screenSettings as any)?.adisyonPrinter;
        
        // Kullanılacak yazıcıyı belirle
        let printerToUse = null;
        if (printingMode === 'single' && defaultPrinter) {
          printerToUse = defaultPrinter;
          console.log('🖨️ [ADİSYON YAZDIR] Tek yazıcı modu - Varsayılan yazıcı kullanılacak:', defaultPrinter.name);
        } else if (printingMode === 'category' && adisyonPrinter) {
          printerToUse = adisyonPrinter;
          console.log('🖨️ [ADİSYON YAZDIR] Kategori modu - Adisyon yazıcısı kullanılacak:', adisyonPrinter.name);
        } else if (defaultPrinter) {
          // Fallback: Adisyon yazıcısı yoksa varsayılan yazıcıyı dene
          printerToUse = defaultPrinter;
          console.log('🖨️ [ADİSYON YAZDIR] Fallback - Varsayılan yazıcı kullanılacak:', defaultPrinter.name);
        }
        
        if (printerToUse) {
          // ✅ localServiceUrl'u screenSettings'den al
          const localServiceUrl = (screenSettings as any)?.localServiceUrl || 'http://localhost:3003';
          await printAdisyonReceipt(adisyonData, printerToUse, localServiceUrl);
          console.log('✅ [ADİSYON YAZDIR] Fiş yazıcıya gönderildi:', printerToUse.name);
        } else {
          console.warn('⚠️ [ADİSYON YAZDIR] Yazıcı tanımlı değil. Yazdırma modu:', printingMode, 
            '| Varsayılan:', defaultPrinter?.name || 'YOK', 
            '| Adisyon:', adisyonPrinter?.name || 'YOK');
        }
      } catch (printError) {
        console.warn('⚠️ [ADİSYON YAZDIR] Fiş yazdırma hatası:', printError);
      }

      const markResult = await restaurantActiveSaleApi.markReceiptPrinted(
        activeSale.Id,
        waiterName || 'Bilinmeyen'
      );

      if (markResult.Success) {
        showSnackbar(`${tableName} adisyonu yazdırıldı! (₺${grandTotal.toFixed(2)})`, 'success');
        setIsReceiptPrintedForCurrentTable(true);
        // ✅ YENİ: Adisyon yazdırma başarılı → masalara dönüş geri sayımını tetikle
        // (mutfak yazdırma akışıyla aynı pattern: kullanıcı geri sayım modalında
        // iptal de edebilir, beklerse otomatik RestaurantScreen'e döner).
        onAfterSuccessfulCheckPrint?.();
      } else {
        showSnackbar(`Adisyon yazdırıldı ama kayıt yapılamadı: ${markResult.Message}`, 'warning');
      }

    } catch (error: any) {
      console.error('❌ [ADİSYON YAZDIR] Hata:', error);
      showSnackbar(`Hata oluştu: ${(error as Error).message}`, 'error');
    }
  }, [isRestaurantMode, cart, tableId, tableName, restaurantScreenId, waiterName, receiptWaiterName, calculateTotal, screenSettings, showSnackbar, setIsReceiptPrintedForCurrentTable, lastAppliedDiscountAmount, appliedDiscountType, discountInputPercentage, onAfterSuccessfulCheckPrint]);

  return {
    handlePrintToKitchen,
    handlePrintCheck,
    handleBackButtonClick,
    handleContinueWithoutPrinting,
    handlePrintToKitchenAndGoBack,
    handlePrintCancellationAndGoBack,
    handlePrintBothAndGoBack,
    handleCloseTable,
    printCancellationForRemovedItem,
  };
};

export default useRestaurantKitchenOperations;
